import React, {useMemo, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {patchSourceConfig} from '@/entities/stream/api';
import {
  settingsPatch,
  type CameraCapabilities,
  type CameraSettings,
  type DayNightMode,
  type VideoStream,
} from '@/entities/stream/settings';
import type {SourceConfig} from '@/entities/stream/types';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {SectionHeader} from '@/shared/ui/section';
import {Segmented} from '@/shared/ui/segmented';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * A camera's basic settings (schema v1).
 *
 * The form starts from what the camera REPORTED, with what the cabinet has asked for
 * on top — so a change still waiting for the camera shows as the value it will become,
 * and a camera nobody configured shows what it actually runs with. Save sends only the
 * difference (`settingsPatch`); the server merges it, bumps the version and wakes the
 * camera through the signalling server.
 *
 * Pickers come from the camera's `capabilities`. Whether 2560×1440 suits THIS model is
 * the camera's call, not ours: when it has not said what it can do, the resolution is
 * shown read-only rather than offered from a guessed list.
 *
 * Field components live at module scope — declared inside the component they become a
 * new type on every keystroke and the keyboard closes after each digit (see
 * TerminalSettingsForm).
 */
const styles = StyleSheet.create({
  root: {gap: spacing(5), paddingBottom: spacing(4)},
  section: {gap: spacing(2)},
  card: {gap: spacing(4)},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  gridItem: {flexBasis: '46%', flexGrow: 1},
  field: {gap: spacing(2)},
  saveRow: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(3)},
});

function clampInt(text: string, min: number, max: number): number {
  const n = parseInt(text.replace(/[^\d]/g, ''), 10);
  if (!Number.isFinite(n)) {
    return min;
  }
  return Math.min(max, Math.max(min, n));
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number | undefined;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <AppInput
      label={label}
      value={value === undefined ? '' : String(value)}
      onChangeText={text => onChange(clampInt(text, min, max))}
      keyboardType="number-pad"
      style={styles.gridItem}
    />
  );
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** `over` on top of `base`, leaf by leaf. */
function overlay<T>(base: T, over: T): T {
  if (!isObject(base) || !isObject(over)) {
    return (over === undefined ? base : over) as T;
  }
  const out: Record<string, unknown> = {...base};
  for (const [k, v] of Object.entries(over)) {
    out[k] = overlay(out[k], v);
  }
  return out as T;
}

const IMAGE_KEYS = ['brightness', 'contrast', 'saturation', 'sharpness'] as const;
const ALL_MODES: DayNightMode[] = ['auto', 'day', 'night'];

function StreamFields({
  title,
  value,
  caps,
  onChange,
}: {
  title: string;
  value: VideoStream | undefined;
  caps: {resolutions?: string[]; fpsMax?: number} | undefined;
  onChange: (next: VideoStream) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const v = value ?? {};
  const resolutions = caps?.resolutions ?? [];
  // A value the camera did not list still has to be selectable, or the picker
  // would show no selection for what the camera is running right now.
  const options =
    v.resolution && !resolutions.includes(v.resolution)
      ? [v.resolution, ...resolutions]
      : resolutions;

  return (
    <View style={styles.field}>
      <AppText variant="label">{title}</AppText>
      {options.length > 0 && v.resolution ? (
        <Segmented
          options={options.map(r => ({value: r, label: r.replace('x', '×')}))}
          value={v.resolution}
          onChange={resolution => onChange({...v, resolution})}
        />
      ) : (
        <AppText variant="caption" color={p.mutedForeground}>
          {v.resolution
            ? t('camera.resolutionFixed', {value: v.resolution.replace('x', '×')})
            : t('camera.resolutionUnknown')}
        </AppText>
      )}
      <View style={styles.grid}>
        <NumberField
          label={t('camera.fps')}
          value={v.fps}
          min={1}
          max={caps?.fpsMax ?? 60}
          onChange={fps => onChange({...v, fps})}
        />
        <NumberField
          label={t('camera.bitrate')}
          value={v.bitrateKbps}
          min={64}
          max={16384}
          onChange={bitrateKbps => onChange({...v, bitrateKbps})}
        />
      </View>
    </View>
  );
}

export function CameraSettingsForm({
  companyId,
  config,
}: {
  companyId: string;
  config: SourceConfig;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const caps: CameraCapabilities = config.capabilities ?? {};

  const base = useMemo(
    () => overlay<CameraSettings>(config.reported ?? {}, config.desired ?? {}),
    [config.reported, config.desired],
  );
  const [draft, setDraft] = useState<CameraSettings>(base);
  const patch = settingsPatch(base, draft);

  function update<K extends keyof CameraSettings>(key: K, value: CameraSettings[K]) {
    setDraft(d => ({...d, [key]: {...(d[key] as object), ...(value as object)}}));
  }

  const save = useMutation({
    mutationFn: () => patchSourceConfig(companyId, config.sourceId, patch ?? {}),
    onSuccess: next => {
      qc.setQueryData(['source-config', companyId, config.sourceId], next);
    },
  });

  const modes = caps.dayNight?.modes?.length ? caps.dayNight.modes : ALL_MODES;
  const audioSupported = caps.audio?.supported !== false;

  return (
    <View style={styles.root}>
      <View style={styles.section}>
        <SectionHeader title={t('camera.sectionImage')} />
        <Card padding={4} style={styles.card}>
          <View style={styles.grid}>
            {IMAGE_KEYS.map(key => (
              <NumberField
                key={key}
                label={t(`camera.image.${key}`)}
                value={draft.image?.[key]}
                min={0}
                max={100}
                onChange={v => update('image', {[key]: v})}
              />
            ))}
          </View>
          <Toggle
            label={t('camera.mirror')}
            value={draft.image?.mirror ?? false}
            onChange={mirror => update('image', {mirror})}
          />
          <Toggle
            label={t('camera.flip')}
            value={draft.image?.flip ?? false}
            onChange={flip => update('image', {flip})}
          />
          <View style={styles.field}>
            <AppText variant="label">{t('camera.dayNight')}</AppText>
            <Segmented
              options={modes.map(m => ({value: m, label: t(`camera.mode.${m}`)}))}
              value={draft.dayNight?.mode ?? 'auto'}
              onChange={mode => update('dayNight', {mode})}
            />
          </View>
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('camera.sectionVideo')} />
        <Card padding={4} style={styles.card}>
          <StreamFields
            title={t('camera.mainStream')}
            value={draft.video?.main}
            caps={caps.video?.main}
            onChange={main => update('video', {main})}
          />
          <StreamFields
            title={t('camera.subStream')}
            value={draft.video?.sub}
            caps={caps.video?.sub}
            onChange={sub => update('video', {sub})}
          />
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('camera.sectionOsd')} />
        <Card padding={4} style={styles.card}>
          <Toggle
            label={t('camera.showTime')}
            value={draft.osd?.showTime ?? false}
            onChange={showTime => update('osd', {showTime})}
          />
          <Toggle
            label={t('camera.showName')}
            value={draft.osd?.showName ?? false}
            onChange={showName => update('osd', {showName})}
          />
          <AppInput
            label={t('camera.osdText')}
            value={draft.osd?.text ?? ''}
            maxLength={64}
            onChangeText={text => update('osd', {text: text.replace(/[\r\n]/g, ' ')})}
          />
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('camera.sectionOther')} />
        <Card padding={4} style={styles.card}>
          {audioSupported ? (
            <Toggle
              label={t('camera.audio')}
              value={draft.audio?.enabled ?? false}
              onChange={enabled => update('audio', {enabled})}
            />
          ) : null}
          <AppInput
            label={t('camera.timezone')}
            value={draft.time?.timezone ?? ''}
            autoCapitalize="none"
            autoCorrect={false}
            placeholder="Asia/Tashkent"
            onChangeText={timezone => update('time', {timezone: timezone.trim()})}
          />
          <AppInput
            label={t('camera.ntpServer')}
            value={draft.time?.ntpServer ?? ''}
            autoCapitalize="none"
            autoCorrect={false}
            placeholder="pool.ntp.org"
            onChangeText={ntpServer => update('time', {ntpServer: ntpServer.trim()})}
          />
        </Card>
      </View>

      {save.isError ? (
        <Banner tone="error" title={messageOf(save.error, t('errors.unknown'))} />
      ) : null}

      <View style={styles.saveRow}>
        <AppButton
          title={t('actions.save')}
          size="lg"
          disabled={!patch}
          loading={save.isPending}
          onPress={() => save.mutate()}
        />
        {/* The Save button sits at the bottom of a long form while the status banner
            is at the top, so the answer to "did the camera take it" is repeated here,
            where the eye is after pressing it. Only once something was saved. */}
        {config.desiredVersion > 0 ? (
          <StatusPill
            tone={
              config.status === 'applied'
                ? 'granted'
                : config.status === 'failed'
                  ? 'denied'
                  : 'pending'
            }
            label={t(`camera.status.${config.status}`)}
          />
        ) : null}
      </View>
    </View>
  );
}
